import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';

import { _ } from '@splunk/ui-utils/i18n';
import Heading from '@splunk/react-ui/Heading';
import Text, {
    type TextBlurHandler,
    type TextChangeHandler,
} from '@splunk/react-ui/Text';
import { variables } from '@splunk/themes';
import type { Mode } from '@splunk/dashboard-types';

import Tooltip from '@splunk/react-ui/Tooltip';
import { useTextInput } from '../hooks/useTextInput';

const StyledHeading = styled(Heading)<{ $isExpanded: boolean }>`
    ${(props) =>
        props.$isExpanded
            ? css`
                  min-height: ${variables.inputHeight};
              `
            : css`
                  height: ${variables.inputHeight};
              `}
    max-width: 100%;
    margin: 0;
    padding-block: 0;
    padding-inline: ${variables.spacingSmall} ${variables.spacingMedium};
    box-sizing: inherit;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: ${(props) => (props.$isExpanded ? 'normal' : 'nowrap')};
`;

const StyledTitleInput = styled(Text)`
    flex-grow: 1;
    width: 100%;
    min-height: ${variables.inputHeight};
    height: ${variables.inputHeight};
    border: none;

    input {
        font-size: ${variables.fontSizeXXLarge};
        font-weight: ${variables.fontWeightBold};
        line-height: ${variables.inputHeight};
    }
`;

const tooltipStyle = { flexGrow: 1, width: '100%' };
const tooltipPointTo = { x: 0, y: 0 };

export interface TitleProps {
    isExpanded?: boolean;
    mode: Mode;
    onValueChange: (value?: string) => void;
    value?: string;
}

export const Title = React.forwardRef<HTMLElement, TitleProps>(
    (
        { isExpanded = false, mode, onValueChange, value = '' },
        ref
    ): JSX.Element => {
        const handleTitleCommit = useCallback(
            (nextValue?: string) => {
                onValueChange(nextValue);
            },
            [onValueChange]
        );

        const {
            onHandleBlur,
            onHandleChange,
            onHandleKeyDown,
            value: inputValue,
        } = useTextInput({
            initialValue: value,
            handleCommit: handleTitleCommit,
            uniqKey: value || '',
        });

        if (mode === 'edit') {
            return (
                <Tooltip
                    content={_('Dashboard title')}
                    inline={false}
                    style={tooltipStyle}
                    pointTo={tooltipPointTo}
                >
                    <StyledTitleInput
                        elementRef={ref as unknown as React.Ref<HTMLDivElement>}
                        data-test="dashboard-title-input"
                        onBlur={onHandleBlur as TextBlurHandler}
                        onChange={onHandleChange as TextChangeHandler}
                        onKeyDown={onHandleKeyDown}
                        placeholder={_('Untitled Dashboard')}
                        aria-label={_('Dashboard title')}
                        value={inputValue}
                    />
                </Tooltip>
            );
        }

        return (
            <StyledHeading
                elementRef={ref as unknown as React.Ref<HTMLHeadingElement>}
                level={2}
                variant="title1"
                $isExpanded={isExpanded}
                data-test="dashboard-title"
            >
                {inputValue}
            </StyledHeading>
        );
    }
);
